import React, { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  Form,
  Input,
  Button,
  Space,
  Tabs,
  Row,
  Col,
  App,
  Switch,
  Modal,
  Table,
  Tag,
  Popconfirm,
  Select,
  Upload,
} from 'antd';
import type { TabsProps } from 'antd';
import {
  SaveOutlined,
  ReloadOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  HeartOutlined,
  UploadOutlined,
  DeleteOutlined as DeleteIcon,
  EyeOutlined,
  HolderOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { aboutContentApi } from '../../services/api';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const { Title } = Typography;
const { TextArea } = Input;

// Sortable Row Component
const SortableRow = ({ children, ...props }: any) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props['data-row-key'] });

  const style = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr {...props} ref={setNodeRef} style={style} {...attributes}>
      {React.Children.map(children, (child, index) => {
        if (index === 0) {
          return React.cloneElement(child, {
            children: (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div
                  {...listeners}
                  style={{
                    cursor: 'grab',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <HolderOutlined />
                </div>
                {child.props.children}
              </div>
            ),
          });
        }
        return child;
      })}
    </tr>
  );
};

interface Value {
  id?: string; // Optional, auto-generated by backend
  title: { en: string; zh: string };
  description: { en: string; zh: string };
  icon?: string; // Optional, for backward compatibility
  isActive: boolean;
  sortOrder: number;
  _id?: string; // MongoDB field
}


const AdminAboutContent: React.FC = () => {
  const [form] = Form.useForm();
  const [valueForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [valueModalVisible, setValueModalVisible] = useState(false);
  const [editingValue, setEditingValue] = useState<Value | null>(null);
  const [mediaTypeEn, setMediaTypeEn] = useState('video');
  const [mediaTypeZh, setMediaTypeZh] = useState('video');
  const [mediaUrlEn, setMediaUrlEn] = useState('');
  const [mediaUrlZh, setMediaUrlZh] = useState('');
  const [localValues, setLocalValues] = useState<Value[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const queryClient = useQueryClient();
  const { message: messageApi } = App.useApp();

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch about content
  const { data: contentData, isLoading } = useQuery({
    queryKey: ['aboutContent'],
    queryFn: aboutContentApi.getContent
  });

  const content = contentData?.content || null;

  // Update form when content loads
  useEffect(() => {
    console.log('useEffect triggered, content:', content);
    if (content) {
      console.log('Setting form values:', content);
      form.setFieldsValue(content);
      setMediaTypeEn(content.overview?.mediaType?.en || 'video');
      setMediaTypeZh(content.overview?.mediaType?.zh || 'video');
      setMediaUrlEn(content.overview?.mediaUrl?.en || '');
      setMediaUrlZh(content.overview?.mediaUrl?.zh || '');
      setLocalValues(content.values || []);
      setHasUnsavedChanges(false);
    }
  }, [content, form]);

  // Update content mutation
  const updateContentMutation = useMutation({
    mutationFn: aboutContentApi.updateContent,
    onSuccess: () => {
      messageApi.success('About content updated successfully');
      queryClient.invalidateQueries({ queryKey: ['aboutContent'] });
    },
    onError: (error: any) => {
      messageApi.error(error.response?.data?.message || 'Failed to update about content');
    }
  });




  const handleSubmit = (values: any) => {
    console.log('Submitting values:', values);
    // Include local values in the submission, but remove _id fields
    const cleanedValues = localValues.map(value => {
      const { _id, ...cleanValue } = value;
      return cleanValue;
    });
    
    const valuesToSubmit = {
      ...values,
      values: cleanedValues
    };
    setLoading(true);
    updateContentMutation.mutate(valuesToSubmit, {
      onSettled: () => {
        setLoading(false);
        setHasUnsavedChanges(false);
      }
    });
  };

  const handleValueSubmit = (values: any) => {
    if (editingValue) {
      // Update existing value in local state
      const updatedValues = localValues.map(value => 
        value.id === editingValue.id 
          ? { ...value, ...values }
          : value
      );
      setLocalValues(updatedValues);
      setHasUnsavedChanges(true);
    } else {
      // Add new value to local state (ID will be auto-generated by backend)
      const newValue: Value = {
        ...values,
        sortOrder: localValues.length + 1
      };
      setLocalValues([...localValues, newValue]);
      setHasUnsavedChanges(true);
    }
    
    // Close modal and reset form
    setValueModalVisible(false);
    setEditingValue(null);
    valueForm.resetFields();
  };

  // Handle drag end for reordering (local only)
  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = localValues.findIndex((value: Value) => String(value.id || value._id || value.sortOrder) === active.id);
      const newIndex = localValues.findIndex((value: Value) => String(value.id || value._id || value.sortOrder) === over.id);

      if (oldIndex !== undefined && newIndex !== undefined) {
        const newValues = arrayMove(localValues, oldIndex, newIndex);
        
        // Update sort order based on new positions
        const updatedValues = newValues.map((value, index) => ({
          ...value,
          sortOrder: index + 1
        }));

        // Update local state only
        setLocalValues(updatedValues);
        setHasUnsavedChanges(true);
      }
    }
  };


  const handleEditValue = (value: Value) => {
    setEditingValue(value);
    valueForm.setFieldsValue(value);
    setValueModalVisible(true);
  };


  const handleDeleteValue = (key: string) => {
    // Remove value from local state using the same key logic
    const updatedValues = localValues.filter(value => String(value.id || value._id || value.sortOrder) !== key);
    // Update sort order for remaining values
    const reorderedValues = updatedValues.map((value, index) => ({
      ...value,
      sortOrder: index + 1
    }));
    setLocalValues(reorderedValues);
    setHasUnsavedChanges(true);
  };


  const valueColumns = [
    {
      title: 'Title (EN)',
      dataIndex: ['title', 'en'],
      key: 'titleEn',
    },
    {
      title: 'Title (ZH)',
      dataIndex: ['title', 'zh'],
      key: 'titleZh',
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Value) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditValue(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this value?"
            onConfirm={() => handleDeleteValue(String(record.id || record._id || record.sortOrder))}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];


  const tabItems: TabsProps['items'] = [
    {
      key: 'overview',
      label: (
        <span>
          <InfoCircleOutlined />
          About Our Healthcare Innovation
        </span>
      ),
      children: (
        <div>
          <Row gutter={24}>
            <Col span={24}>
              <Card title="About Our Healthcare Innovation Section" size="small">
                <Form.Item 
                  label="Title (English)" 
                  name={['overview', 'title', 'en']}
                  rules={[{ required: true, message: 'Please enter English title' }]}
                >
                  <Input placeholder="Enter English title" />
                </Form.Item>
                <Form.Item 
                  label="Title (Chinese)" 
                  name={['overview', 'title', 'zh']}
                  rules={[{ required: true, message: 'Please enter Chinese title' }]}
                >
                  <Input placeholder="輸入中文標題" />
                </Form.Item>
                <Form.Item 
                  label="Description (English)" 
                  name={['overview', 'content', 'en']}
                  rules={[{ required: true, message: 'Please enter English description' }]}
                >
                  <TextArea 
                    rows={6} 
                    placeholder="Enter English description (press Enter for line breaks)" 
                    style={{ whiteSpace: 'pre-wrap' }}
                  />
                </Form.Item>
                <Form.Item 
                  label="Description (Chinese)" 
                  name={['overview', 'content', 'zh']}
                  rules={[{ required: true, message: 'Please enter Chinese description' }]}
                >
                  <TextArea 
                    rows={6} 
                    placeholder="輸入中文描述（按 Enter 換行）" 
                    style={{ whiteSpace: 'pre-wrap' }}
                  />
                </Form.Item>
              </Card>
            </Col>
          </Row>
          
          <Row gutter={24} style={{ marginTop: 24 }}>
            <Col span={24}>
              <Card title="Media Content (Right Side)" size="small">
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item 
                      label="Media Type (English)" 
                      name={['overview', 'mediaType', 'en']}
                    >
                      <Select 
                        placeholder="Select media type"
                        onChange={(value) => {
                          const currentMediaUrl = form.getFieldValue(['overview', 'mediaUrl', 'en']);
                          if (currentMediaUrl && currentMediaUrl !== '/aboutusvideo.mp4' && currentMediaUrl !== '') {
                            Modal.confirm({
                              title: 'Switch Media Type',
                              content: 'Changing media type will clear the current media. Are you sure?',
                              onOk: () => {
                                setMediaTypeEn(value);
                                form.setFieldValue(['overview', 'mediaUrl', 'en'], '');
                                setMediaUrlEn('');
                              }
                            });
                          } else {
                            setMediaTypeEn(value);
                            form.setFieldValue(['overview', 'mediaUrl', 'en'], '');
                            setMediaUrlEn('');
                          }
                        }}
                      >
                        <Select.Option value="video">Video</Select.Option>
                        <Select.Option value="image">Image</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item 
                      label="Media Type (Chinese)" 
                      name={['overview', 'mediaType', 'zh']}
                    >
                      <Select 
                        placeholder="選擇媒體類型"
                        onChange={(value) => {
                          const currentMediaUrl = form.getFieldValue(['overview', 'mediaUrl', 'zh']);
                          if (currentMediaUrl && currentMediaUrl !== '/aboutusvideo.mp4' && currentMediaUrl !== '') {
                            Modal.confirm({
                              title: 'Switch Media Type',
                              content: 'Changing media type will clear the current media. Are you sure?',
                              onOk: () => {
                                setMediaTypeZh(value);
                                form.setFieldValue(['overview', 'mediaUrl', 'zh'], '');
                                setMediaUrlZh('');
                              }
                            });
                          } else {
                            setMediaTypeZh(value);
                            form.setFieldValue(['overview', 'mediaUrl', 'zh'], '');
                            setMediaUrlZh('');
                          }
                        }}
                      >
                        <Select.Option value="video">視頻</Select.Option>
                        <Select.Option value="image">圖片</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item 
                      label="Media File (English)" 
                      name={['overview', 'mediaUrl', 'en']}
                    >
                      <div>
                        {mediaUrlEn && mediaUrlEn !== '/aboutusvideo.mp4' && mediaUrlEn !== '' ? (
                          <div className="mb-4">
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded border">
                              <div className="flex items-center space-x-3">
                                <EyeOutlined className="text-blue-500" />
                                <span className="text-sm font-medium">
                                  {mediaTypeEn === 'image' ? 'Image' : 'Video'} Preview
                                </span>
                              </div>
                              <Button 
                                type="text" 
                                danger 
                                icon={<DeleteIcon />}
                                onClick={() => {
                                  form.setFieldValue(['overview', 'mediaUrl', 'en'], '/aboutusvideo.mp4');
                                  setMediaUrlEn('/aboutusvideo.mp4');
                                }}
                              >
                                Delete
                              </Button>
                            </div>
                            <div className="mt-2">
                              {mediaTypeEn === 'image' ? (
                                <img 
                                  src={mediaUrlEn} 
                                  alt="Preview" 
                                  className="w-full h-32 object-cover rounded"
                                />
                              ) : (
                                <video 
                                  src={mediaUrlEn} 
                                  className="w-full h-32 object-cover rounded"
                                  controls
                                />
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500 mb-2">Using default media</div>
                        )}
                        <Upload
                          accept={mediaTypeEn === 'image' ? 'image/*' : 'video/*'}
                          beforeUpload={(file) => {
                            console.log('Uploading file:', file);
                            const reader = new FileReader();
                            reader.onload = (e) => {
                              const result = e.target?.result as string;
                              console.log('File converted to base64, length:', result?.length);
                              form.setFieldValue(['overview', 'mediaUrl', 'en'], result);
                              setMediaUrlEn(result); // Update state for immediate preview
                            };
                            reader.readAsDataURL(file);
                            return false; // Prevent auto upload
                          }}
                          showUploadList={false}
                        >
                          <Button icon={<UploadOutlined />}>
                            Upload {mediaTypeEn === 'image' ? 'Image' : 'Video'}
                          </Button>
                        </Upload>
                      </div>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item 
                      label="Media File (Chinese)" 
                      name={['overview', 'mediaUrl', 'zh']}
                    >
                      <div>
                        {mediaUrlZh && mediaUrlZh !== '/aboutusvideo.mp4' && mediaUrlZh !== '' ? (
                          <div className="mb-4">
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded border">
                              <div className="flex items-center space-x-3">
                                <EyeOutlined className="text-blue-500" />
                                <span className="text-sm font-medium">
                                  {mediaTypeZh === 'image' ? '圖片預覽' : '視頻預覽'}
                                </span>
                              </div>
                              <Button 
                                type="text" 
                                danger 
                                icon={<DeleteIcon />}
                                onClick={() => {
                                  form.setFieldValue(['overview', 'mediaUrl', 'zh'], '/aboutusvideo.mp4');
                                  setMediaUrlZh('/aboutusvideo.mp4');
                                }}
                              >
                                刪除
                              </Button>
                            </div>
                            <div className="mt-2">
                              {mediaTypeZh === 'image' ? (
                                <img 
                                  src={mediaUrlZh} 
                                  alt="預覽" 
                                  className="w-full h-32 object-cover rounded"
                                />
                              ) : (
                                <video 
                                  src={mediaUrlZh} 
                                  className="w-full h-32 object-cover rounded"
                                  controls
                                />
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500 mb-2">使用默認媒體</div>
                        )}
                        <Upload
                          accept={mediaTypeZh === 'image' ? 'image/*' : 'video/*'}
                          beforeUpload={(file) => {
                            console.log('Uploading Chinese file:', file);
                            const reader = new FileReader();
                            reader.onload = (e) => {
                              const result = e.target?.result as string;
                              console.log('Chinese file converted to base64, length:', result?.length);
                              form.setFieldValue(['overview', 'mediaUrl', 'zh'], result);
                              setMediaUrlZh(result); // Update state for immediate preview
                            };
                            reader.readAsDataURL(file);
                            return false; // Prevent auto upload
                          }}
                          showUploadList={false}
                        >
                          <Button icon={<UploadOutlined />}>
                            上傳 {mediaTypeZh === 'image' ? '圖片' : '視頻'}
                          </Button>
                        </Upload>
                      </div>
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: 'values',
      label: (
        <span>
          <HeartOutlined />
          Our Values Section
        </span>
      ),
      children: (
        <div>
          <Row gutter={24}>
            <Col span={24}>
              <Card title="Our Values Section Settings" size="small">
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item 
                      label="Section Title (English)" 
                      name={['valuesSection', 'title', 'en']}
                      rules={[{ required: true, message: 'Please enter English title' }]}
                    >
                      <Input placeholder="e.g., Our Values" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item 
                      label="Section Title (Chinese)" 
                      name={['valuesSection', 'title', 'zh']}
                      rules={[{ required: true, message: 'Please enter Chinese title' }]}
                    >
                      <Input placeholder="e.g., 我們的價值觀" />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item 
                      label="Section Subtitle (English)" 
                      name={['valuesSection', 'subtitle', 'en']}
                      rules={[{ required: true, message: 'Please enter English subtitle' }]}
                    >
                      <Input placeholder="e.g., To improve lives and contribute to a better society" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item 
                      label="Section Subtitle (Chinese)" 
                      name={['valuesSection', 'subtitle', 'zh']}
                      rules={[{ required: true, message: 'Please enter Chinese subtitle' }]}
                    >
                      <Input placeholder="e.g., 改善生活，為更美好的社會做出貢獻" />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
          
          <Row gutter={24} style={{ marginTop: 24 }}>
            <Col span={24}>
              <Card title="Values Content" size="small">
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Title level={4}>Individual Values</Title>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setEditingValue(null);
                      valueForm.resetFields();
                      setValueModalVisible(true);
                    }}
                  >
                    Add Value
                  </Button>
                </div>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={localValues.map((value, index) => String(value.id || value._id || index))}
                    strategy={verticalListSortingStrategy}
                  >
                    <Table
                      columns={valueColumns}
                      dataSource={localValues}
                      rowKey={(record) => record.id || record._id || record.sortOrder}
                      pagination={false}
                      loading={isLoading}
                      components={{
                        body: {
                          row: SortableRow,
                        },
                      }}
                    />
                  </SortableContext>
                </DndContext>
              </Card>
            </Col>
          </Row>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2}>About Us Content Management</Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => queryClient.invalidateQueries({ queryKey: ['aboutContent'] })}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={loading || updateContentMutation.isPending}
            onClick={() => form.submit()}
          >
            {hasUnsavedChanges ? 'Save Changes' : 'Save Settings'}
          </Button>
        </Space>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          items={tabItems}
        />
      </Form>

      {/* Value Modal */}
      <Modal
        title={editingValue ? 'Edit Company Value' : 'Add Company Value'}
        open={valueModalVisible}
        onCancel={() => {
          setValueModalVisible(false);
          setEditingValue(null);
          valueForm.resetFields();
        }}
        footer={null}
        width={800}
      >
        <Form
          form={valueForm}
          layout="vertical"
          onFinish={handleValueSubmit}
        >
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Title (English)" name={['title', 'en']} rules={[{ required: true, message: 'Please enter English title' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Title (Chinese)" name={['title', 'zh']} rules={[{ required: true, message: 'Please enter Chinese title' }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item label="Description (English)" name={['description', 'en']} rules={[{ required: true, message: 'Please enter English description' }]}>
            <TextArea rows={4} />
          </Form.Item>
          
          <Form.Item label="Description (Chinese)" name={['description', 'zh']} rules={[{ required: true, message: 'Please enter Chinese description' }]}>
            <TextArea rows={4} />
          </Form.Item>
          
          <Form.Item label="Active" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>
          
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setValueModalVisible(false);
                setEditingValue(null);
                valueForm.resetFields();
              }}>
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
              >
                {editingValue ? 'Update' : 'Add'} Value
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

    </div>
  );
};

export default AdminAboutContent;
